import { useRef, useLayoutEffect, useState } from 'react';
// One-third of the next item peeks below the fold to signal that the list is scrollable.
const PEEK_FRACTION = 1 / 3;
// Computes a maxHeight that shows exactly `maxItems` full visible iterations plus a partial
// peek of the next one. Measures actual DOM children so conditionally-hidden items (e.g. via
// an If child) don't contribute to the count or the height calculation. A ResizeObserver
// re-runs the measurement whenever the set of visible items changes.
export function useLoopScroll(maxItems) {
  const containerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(null);
  useLayoutEffect(() => {
    if (maxItems === undefined || !containerRef.current) {
      setMaxHeight(null);
      return;
    }
    const measure = () => {
      if (!containerRef.current) return;
      const children = Array.from(containerRef.current.children);
      if (children.length <= maxItems) {
        setMaxHeight(null);
        return;
      }
      const lastFullItem = children[maxItems - 1];
      const peekItem = children[maxItems];
      if (!lastFullItem || !peekItem) return;
      const containerTop = containerRef.current.getBoundingClientRect().top;
      const fullItemsBottom = lastFullItem.getBoundingClientRect().bottom;
      const peekItemHeight = peekItem.getBoundingClientRect().height;
      if (peekItemHeight === 0) return;
      setMaxHeight(fullItemsBottom - containerTop + peekItemHeight * PEEK_FRACTION);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [maxItems]);
  const scrollContainerStyle = maxHeight !== null ? {
    maxHeight,
    overflowY: 'auto'
  } : {};
  return {
    containerRef,
    scrollContainerStyle
  };
}